import { Layout } from "@/components/layout";
import { SEOHead } from "@/components/seo-head";
import {
  PRICING,
  VERTICAL_NATIVE_PRICING,
  WORD_ADDIN_PRICING,
  type MarketingPlanCode,
} from "@/lib/content";
import { Button } from "@/components/ui/button";
import { CheckCircle2, ArrowUpRight, Building2, HardHat, Shield, ArrowRight } from "lucide-react";
import { motion } from "framer-motion";
import { Link } from "wouter";
import { useState, useEffect } from "react";
import { PricingCard } from "@/components/pricing/pricing-card";
import { BillingToggle } from "@/components/pricing/billing-toggle";
import { Faqs } from "@/components/pricing/faqs";
import { WordAddinWaitlistDialog } from "@/components/word-addin-waitlist-dialog";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { AlertCircle, Loader2 } from "lucide-react";

const verticalCheckoutSchema = z.object({
  vertical: z.enum(["construction", "insurance"], {
    errorMap: () => ({ message: "Please choose Construction or Insurance." }),
  }),
});

/**
 * Map a vertical-native tier display name (Solo / Team / Pro) plus a
 * vertical pick to the marketing plan slug the api-server's
 * `/marketing/checkout` endpoint accepts. Mirrors the vertical-tier
 * SKUs declared in `MARKETING_CHECKOUT_PLANS`.
 */
function planForVerticalTier(
  vertical: "construction" | "insurance",
  tier: string,
): MarketingPlanCode {
  const normalised = tier.trim().toLowerCase();
  if (normalised === "solo" || normalised === "team" || normalised === "pro") {
    return `${vertical}_${normalised}` as MarketingPlanCode;
  }
  // Defensive default — should never happen because the call sites only
  // pass plan.name from VERTICAL_NATIVE_PRICING.plans (Solo|Team|Pro).
  return `${vertical}_solo` as MarketingPlanCode;
}

/**
 * Hits the api-server's public Stripe Checkout endpoint and redirects
 * the buyer to Stripe-hosted checkout. Mirrors the helper in
 * `pricing-vertical.tsx` so the platform pricing page's vertical-native
 * section uses the same real-checkout path (no waitlist fallback).
 */
async function startVerticalNativeCheckout(opts: {
  plan: MarketingPlanCode;
  vertical: "construction" | "insurance";
}): Promise<void> {
  const origin = window.location.origin;
  const successUrl = `${origin}/app/register?checkout_session={CHECKOUT_SESSION_ID}`;
  const cancelUrl = `${origin}/pricing?canceled=1#${VERTICAL_NATIVE_PRICING.sectionId}`;
  const res = await fetch("/api/marketing/checkout", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      plan: opts.plan,
      // The vertical-native section markets a single monthly price;
      // checkout always opens on the monthly cadence here so we don't
      // surprise the buyer with an annual commit they didn't see priced.
      // The dedicated /pricing/<vertical> pages still expose the toggle.
      billing: "monthly",
      successUrl,
      cancelUrl,
    }),
  });
  const body = (await res.json().catch(() => ({}))) as {
    url?: string;
    error?: string;
  };
  if (!res.ok || !body.url) {
    throw new Error(body.error || "Could not start checkout");
  }
  window.location.assign(body.url);
}

const vdlg = VERTICAL_NATIVE_PRICING.signupDialog;

/**
 * Vertical-native checkout dialog. Replaces the previous waitlist
 * variant — clicking "Continue to checkout" now POSTs to the
 * api-server's `/marketing/checkout` endpoint (same path the
 * dedicated `/pricing/<vertical>` page uses) and redirects the buyer
 * straight to Stripe-hosted checkout. The vertical the buyer picks
 * here, combined with the tier they clicked, decides which Stripe
 * SKU is opened (e.g. construction_team / insurance_solo).
 */
function VerticalCheckoutDialog({
  open,
  onOpenChange,
  selectedTier,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedTier: string;
}) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const form = useForm<z.infer<typeof verticalCheckoutSchema>>({
    resolver: zodResolver(verticalCheckoutSchema),
    defaultValues: { vertical: undefined as unknown as "construction" },
  });

  async function onSubmit(values: z.infer<typeof verticalCheckoutSchema>) {
    setIsSubmitting(true);
    setSubmitError(null);
    try {
      const plan = planForVerticalTier(values.vertical, selectedTier);
      // NOTE: we deliberately don't fire a marketing analytics event
      // here — the existing PostHog `track()` schema only covers
      // form-submission events, and the SUCCESS signal we actually
      // care about (Stripe Checkout completed → tenant provisioned)
      // is already captured server-side in `register-from-checkout`
      // analytics. Wiring a `marketing_checkout_started` event is
      // tracked in the analytics-events spec but out of scope for
      // pilot readiness.
      await startVerticalNativeCheckout({ plan, vertical: values.vertical });
      // No success state — the page is being unloaded by the redirect to
      // Stripe; clearing isSubmitting would briefly re-enable the button
      // mid-navigation.
    } catch (err) {
      setSubmitError(
        err instanceof Error
          ? err.message
          : "Could not start checkout. Please try again.",
      );
      setIsSubmitting(false);
    }
  }

  function handleOpenChange(open: boolean) {
    if (!open) {
      form.reset();
      setSubmitError(null);
      setIsSubmitting(false);
    }
    onOpenChange(open);
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[440px]">
        <DialogHeader>
          <div className="flex items-center gap-2 mb-1">
            <div className="h-8 w-8 rounded-lg bg-brand-gradient flex items-center justify-center">
              <Building2 className="h-4 w-4 text-white" />
            </div>
            <span className="text-xs font-semibold text-indigo-600 dark:text-indigo-400 uppercase tracking-wider">
              {vdlg.eyebrow}
            </span>
          </div>
          <DialogTitle className="text-xl">
            Start your {selectedTier ? `${selectedTier} ` : ""}workspace
          </DialogTitle>
          <DialogDescription>
            Pick your industry and we&rsquo;ll open a secure Stripe Checkout
            for the matching {selectedTier ? `${selectedTier} ` : ""}plan
            with a 14-day trial. We&rsquo;ll email you a sign-in link the
            moment payment confirms.
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="space-y-4 pt-2"
            data-testid="vertical-checkout-form"
          >
            <FormField
              control={form.control}
              name="vertical"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{vdlg.verticalLabel}</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger data-testid="select-vertical-checkout-industry">
                        <SelectValue placeholder="Choose your industry" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {vdlg.verticalOptions.map((opt) => (
                        <SelectItem
                          key={opt.value}
                          value={opt.value}
                          data-testid={`select-vertical-checkout-industry-${opt.value}`}
                        >
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            {submitError && (
              <div
                className="flex items-start gap-3 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-900/50 dark:bg-red-950/30 dark:text-red-400"
                data-testid="vertical-checkout-error"
                role="alert"
              >
                <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
                <span>{submitError}</span>
              </div>
            )}

            <Button
              type="submit"
              className="w-full bg-brand-gradient text-white border-0 hover:opacity-90"
              disabled={isSubmitting}
              data-testid="button-vertical-checkout-submit"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Opening Stripe Checkout…
                </>
              ) : (
                "Continue to checkout"
              )}
            </Button>
            <p className="text-xs text-center text-muted-foreground">
              Monthly self-serve. Cancel anytime. No procurement call.
            </p>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}

export default function PricingPage() {
  const [isAnnual, setIsAnnual] = useState(true);
  const [waitlistOpen, setWaitlistOpen] = useState(false);
  const [verticalOpen, setVerticalOpen] = useState(false);
  const [selectedTier, setSelectedTier] = useState("");
  const [selectedVerticalTier, setSelectedVerticalTier] = useState("");
  // Friendly label ("Solo Counsel" / "Team Pro") shown in the waitlist
  // dialog title; `selectedTier` stays as the stable analytics value.
  const [selectedTierLabel, setSelectedTierLabel] = useState<string>("");

  // Scroll to hash anchor on mount and on hash changes (e.g. SPA nav while already
  // on /pricing). The 100ms delay lets the page fully render before scrolling so
  // elements are in position when scrollIntoView runs.
  useEffect(() => {
    function scrollToHash() {
      const hash = window.location.hash.slice(1);
      if (!hash) return;
      const el = document.getElementById(hash);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
    const timer = setTimeout(scrollToHash, 100);
    window.addEventListener("hashchange", scrollToHash);
    return () => {
      clearTimeout(timer);
      window.removeEventListener("hashchange", scrollToHash);
    };
  }, []);

  function openWaitlist(tier: string, tierLabel?: string) {
    setSelectedTier(tier);
    setSelectedTierLabel(
      tierLabel ??
        WORD_ADDIN_PRICING.plans.find((p) => p.name === tier)?.displayName ??
        tier,
    );
    setWaitlistOpen(true);
  }

  function openVerticalSignup(tier: string) {
    setSelectedVerticalTier(tier);
    setVerticalOpen(true);
  }

  return (
    <Layout>
      <SEOHead title={PRICING.seo.title} description={PRICING.seo.description} />

      <WordAddinWaitlistDialog
        open={waitlistOpen}
        onOpenChange={setWaitlistOpen}
        selectedTier={selectedTier}
        selectedTierLabel={selectedTierLabel}
        source="pricing-page"
      />

      <VerticalCheckoutDialog
        open={verticalOpen}
        onOpenChange={setVerticalOpen}
        selectedTier={selectedVerticalTier}
      />

      <section className="pt-24 pb-20 md:pt-32 md:pb-24 text-center">
        <div className="container mx-auto px-4 md:px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="max-w-3xl mx-auto"
          >
            <div className="text-sm font-semibold text-indigo-600 dark:text-indigo-400 mb-4 tracking-wider uppercase">
              {PRICING.eyebrow}
            </div>
            <h1 className="text-display mb-6 text-navy dark:text-white">
              {PRICING.title}
            </h1>
            <p className="text-body-lg text-slate-600 dark:text-slate-300 mb-10">
              {PRICING.subtitle}
            </p>

            <BillingToggle isAnnual={isAnnual} onChange={setIsAnnual} saveLabel="Save 20%" />

            <div className="mt-8 flex flex-wrap items-center justify-center gap-2 text-xs">
              <span className="text-muted-foreground uppercase tracking-wider font-semibold">
                Jump to:
              </span>
              <a
                href="#platform-tiers"
                className="rounded-full border border-border px-3 py-1 font-medium text-slate-700 dark:text-slate-200 hover:border-indigo-500 hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors"
              >
                Horizontal CLM
              </a>
              <a
                href={`#${VERTICAL_NATIVE_PRICING.sectionId}`}
                className="rounded-full border border-border px-3 py-1 font-medium text-slate-700 dark:text-slate-200 hover:border-indigo-500 hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors"
              >
                Vertical-native (Construction & Insurance)
              </a>
              <a
                href={`#${WORD_ADDIN_PRICING.sectionId}`}
                className="rounded-full border border-border px-3 py-1 font-medium text-slate-700 dark:text-slate-200 hover:border-indigo-500 hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors"
              >
                Word add-in
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      <section id="platform-tiers" className="pb-16 scroll-mt-20">
        <div className="container mx-auto px-4 md:px-6">
          <div className="max-w-6xl mx-auto mb-10 text-center">
            <div className="inline-flex items-center gap-2 mb-3">
              <span className="text-xs font-bold tracking-[0.25em] uppercase text-indigo-600 dark:text-indigo-400">
                Track A · Horizontal CLM
              </span>
            </div>
            <h2 className="text-h2 text-navy dark:text-white max-w-2xl mx-auto">
              Procurement-led plans for general mid-market legal teams.
            </h2>
            <p className="text-sm text-muted-foreground mt-3 max-w-xl mx-auto">
              Annual contracts. Custom DPAs available on Enterprise. Toggle annual billing above to apply the 20% discount.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {PRICING.plans.map((plan, i) => {
              let displayPrice: string = plan.price;
              if (isAnnual && plan.price !== "Custom" && plan.price !== "$0") {
                const numericPrice = parseInt(plan.price.replace('$', ''), 10);
                displayPrice = `$${Math.round(numericPrice * 0.8)}`;
              }

              return (
                <PricingCard
                  key={plan.name}
                  index={i}
                  name={plan.name}
                  price={displayPrice}
                  cadence={plan.cadence}
                  tagline={plan.tagline}
                  features={[...plan.features]}
                  cta={plan.cta}
                  featured={plan.featured}
                />
              );
            })}
          </div>
        </div>
      </section>

      {/* ── Vertical-native Section ── */}
      <section
        id={VERTICAL_NATIVE_PRICING.sectionId}
        className="py-24 bg-gradient-to-b from-indigo-50/40 to-white dark:from-indigo-950/20 dark:to-transparent border-y border-border/50 scroll-mt-16"
      >
        <div className="container mx-auto px-4 md:px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="max-w-6xl mx-auto"
          >
            {/* Section header */}
            <div className="text-center mb-12">
              <div className="inline-flex items-center gap-2 mb-3">
                <span className="text-xs font-bold tracking-[0.25em] uppercase text-indigo-600 dark:text-indigo-400">
                  Track B · {VERTICAL_NATIVE_PRICING.eyebrow}
                </span>
              </div>
              <h2 className="text-h2 mb-4 text-navy dark:text-white max-w-3xl mx-auto">
                {VERTICAL_NATIVE_PRICING.heading}
              </h2>
              <p className="text-body-lg text-slate-600 dark:text-slate-300 max-w-2xl mx-auto">
                {VERTICAL_NATIVE_PRICING.subheading}
              </p>
            </div>

            {/* Vertical-native pricing cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto mb-8">
              {VERTICAL_NATIVE_PRICING.plans.map((plan, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.1 }}
                  className={`flex flex-col rounded-3xl p-8 ${
                    plan.featured
                      ? 'border-2 border-indigo-500 shadow-xl relative'
                      : 'border border-border shadow-sm'
                  } bg-card text-card-foreground`}
                >
                  {plan.featured && (
                    <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-indigo-500 text-white text-xs font-bold uppercase tracking-wider py-1 px-3 rounded-full">
                      Most Popular
                    </div>
                  )}
                  <h3 className="text-xl font-bold mb-2">{plan.name}</h3>
                  <div className="mb-1">
                    <span className="text-4xl font-extrabold">{plan.price}</span>
                    <span className="text-sm text-muted-foreground ml-1">{plan.cadence}</span>
                  </div>
                  {plan.billing && (
                    <div className="text-xs text-muted-foreground mb-3">{plan.billing}</div>
                  )}
                  <p className="text-sm text-muted-foreground mb-8 min-h-[40px]">
                    {plan.tagline}
                  </p>
                  <div className="mb-8 mt-auto">
                    <Button
                      onClick={() => openVerticalSignup(plan.name)}
                      className={`w-full ${plan.featured ? 'bg-indigo-600 hover:bg-indigo-700 text-white' : ''}`}
                      variant={plan.featured ? 'default' : 'outline'}
                    >
                      {plan.cta.label}
                    </Button>
                  </div>
                  <div className="text-sm font-semibold mb-4">What's included</div>
                  <ul className="space-y-3">
                    {plan.features.map((feature, j) => (
                      <li key={j} className="flex items-start gap-3 text-sm">
                        <CheckCircle2 className="h-5 w-5 text-indigo-500 shrink-0" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </motion.div>
              ))}
            </div>

            {/* Pay-as-you-go callout */}
            <div className="max-w-3xl mx-auto rounded-2xl border border-indigo-200 dark:border-indigo-900/50 bg-indigo-50/60 dark:bg-indigo-950/30 p-5 text-center mb-8">
              <p className="text-sm text-slate-700 dark:text-slate-200">
                {VERTICAL_NATIVE_PRICING.payAsYouGoLine}
              </p>
            </div>

            {/* Cross-link to platform tiers */}
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="max-w-3xl mx-auto rounded-2xl border border-border bg-card p-8"
            >
              <h3 className="text-base font-bold mb-2">
                {VERTICAL_NATIVE_PRICING.platformDiff.heading}
              </h3>
              <p className="text-sm text-muted-foreground mb-5">
                {VERTICAL_NATIVE_PRICING.platformDiff.body}
              </p>
              <a
                href={VERTICAL_NATIVE_PRICING.platformDiff.cta.anchor}
                className="text-sm font-semibold text-indigo-600 dark:text-indigo-400 hover:underline inline-flex items-center gap-1"
              >
                {VERTICAL_NATIVE_PRICING.platformDiff.cta.label}
                <ArrowUpRight className="h-3.5 w-3.5" />
              </a>
            </motion.div>

            {/* Per-vertical deep-dive callout (links to dedicated /pricing/<vertical> pages) */}
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="max-w-5xl mx-auto mt-8 rounded-3xl border border-indigo-200 dark:border-indigo-900/50 bg-indigo-50/40 dark:bg-indigo-950/20 p-8 md:p-10"
              data-testid="vertical-pricing-callout"
            >
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
                <div className="md:max-w-xl">
                  <div className="text-xs font-semibold uppercase tracking-wider text-indigo-600 dark:text-indigo-400 mb-2">
                    Want a deeper dive?
                  </div>
                  <h3 className="text-h3 mb-2 text-navy dark:text-white">
                    See industry-specific pricing pages
                  </h3>
                  <p className="text-sm text-slate-600 dark:text-slate-300">
                    Each vertical has a dedicated page with the included pack, sample contract types, and ROI examples for that industry.
                  </p>
                </div>
                <div className="flex flex-col sm:flex-row gap-3 shrink-0">
                  <Link href="/pricing/construction">
                    <Button
                      variant="outline"
                      className="w-full sm:w-auto h-11 px-5 bg-white dark:bg-card"
                      data-testid="vertical-pricing-callout-construction"
                    >
                      <HardHat className="h-4 w-4 mr-2" />
                      Construction pricing
                      <ArrowRight className="h-4 w-4 ml-2" />
                    </Button>
                  </Link>
                  <Link href="/pricing/insurance">
                    <Button
                      variant="outline"
                      className="w-full sm:w-auto h-11 px-5 bg-white dark:bg-card"
                      data-testid="vertical-pricing-callout-insurance"
                    >
                      <Shield className="h-4 w-4 mr-2" />
                      Insurance pricing
                      <ArrowRight className="h-4 w-4 ml-2" />
                    </Button>
                  </Link>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ── Word Add-in Section ── */}
      <section
        id={WORD_ADDIN_PRICING.sectionId}
        className="py-24 bg-slate-50 dark:bg-navy/20 border-y border-border/50 scroll-mt-16"
      >
        <div className="container mx-auto px-4 md:px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="max-w-6xl mx-auto"
          >
            {/* Section header */}
            <div className="text-center mb-12">
              <div className="inline-flex items-center gap-2 mb-4">
                <span className="text-sm font-semibold text-indigo-600 dark:text-indigo-400 tracking-wider uppercase">
                  {WORD_ADDIN_PRICING.eyebrow}
                </span>
              </div>
              <h2 className="text-h2 mb-4 text-navy dark:text-white max-w-2xl mx-auto">
                {WORD_ADDIN_PRICING.heading}
              </h2>
              <p className="text-body-lg text-slate-600 dark:text-slate-300 max-w-2xl mx-auto mb-4">
                {WORD_ADDIN_PRICING.subheading}
              </p>
              <p className="text-sm font-medium text-indigo-700 dark:text-indigo-300 italic max-w-xl mx-auto">
                "{WORD_ADDIN_PRICING.defensibilityLine}"
              </p>
            </div>

            {/* Add-in pricing cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-3xl mx-auto mb-12">
              {WORD_ADDIN_PRICING.plans.map((plan, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.1 }}
                  className={`flex flex-col rounded-3xl p-8 ${
                    plan.featured
                      ? 'border-2 border-indigo-500 shadow-xl relative'
                      : 'border border-border shadow-sm'
                  } bg-card text-card-foreground`}
                >
                  {plan.featured && (
                    <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-indigo-500 text-white text-xs font-bold uppercase tracking-wider py-1 px-3 rounded-full">
                      Most Popular
                    </div>
                  )}
                  <h3 className="text-xl font-bold mb-2">{plan.displayName}</h3>
                  <div className="mb-1">
                    <span className="text-4xl font-extrabold">{plan.price}</span>
                    <span className="text-sm text-muted-foreground ml-1">{plan.cadence}</span>
                  </div>
                  {plan.billing && (
                    <div className="text-xs text-muted-foreground mb-3">{plan.billing}</div>
                  )}
                  <p className="text-sm text-muted-foreground mb-8 min-h-[40px]">
                    {plan.tagline}
                  </p>
                  <div className="mb-8 mt-auto">
                    <Button
                      onClick={() => openWaitlist(plan.name, plan.displayName)}
                      className={`w-full ${plan.featured ? 'bg-indigo-600 hover:bg-indigo-700 text-white' : ''}`}
                      variant={plan.featured ? 'default' : 'outline'}
                    >
                      {plan.cta.label}
                    </Button>
                  </div>
                  <div className="text-sm font-semibold mb-4">What's included</div>
                  <ul className="space-y-3">
                    {plan.features.map((feature, j) => (
                      <li key={j} className="flex items-start gap-3 text-sm">
                        <CheckCircle2 className="h-5 w-5 text-indigo-500 shrink-0" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                  {plan.notIncluded && (
                    <p className="mt-4 text-xs text-muted-foreground border-t border-border pt-4">
                      {plan.notIncluded}
                    </p>
                  )}
                </motion.div>
              ))}
            </div>

            {/* Platform diff callout */}
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="max-w-3xl mx-auto rounded-2xl border border-border bg-card p-8"
            >
              <h3 className="text-base font-bold mb-2">
                {WORD_ADDIN_PRICING.platformDiff.heading}
              </h3>
              <p className="text-sm text-muted-foreground mb-5">
                {WORD_ADDIN_PRICING.platformDiff.body}
              </p>
              <ul className="space-y-2 mb-6">
                {WORD_ADDIN_PRICING.platformDiff.bullets.map((bullet, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-slate-700 dark:text-slate-300">
                    <ArrowUpRight className="h-4 w-4 text-indigo-500 shrink-0 mt-0.5" />
                    <span>{bullet}</span>
                  </li>
                ))}
              </ul>
              <a
                href={WORD_ADDIN_PRICING.platformDiff.cta.anchor}
                className="text-sm font-semibold text-indigo-600 dark:text-indigo-400 hover:underline inline-flex items-center gap-1"
              >
                {WORD_ADDIN_PRICING.platformDiff.cta.label}
                <ArrowUpRight className="h-3.5 w-3.5" />
              </a>
            </motion.div>

            {/* Cross-link to the dedicated Word add-in page */}
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="max-w-3xl mx-auto mt-6 text-center"
            >
              <Link
                href="/word-addin"
                className="inline-flex items-center gap-1 text-sm font-semibold text-indigo-600 dark:text-indigo-400 hover:underline"
                data-testid="pricing-word-addin-page-link"
              >
                See the full Word add-in page
                <ArrowRight className="h-3.5 w-3.5" />
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>

      <section className="py-24 bg-slate-50 dark:bg-navy/30 border-b border-border/50">
        <div className="container mx-auto px-4 md:px-6">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-h2 text-center mb-12">Feature Comparison</h2>
            <div className="space-y-12">
              {PRICING.comparison.map((group, i) => (
                <div key={i}>
                  <h3 className="text-lg font-bold mb-4 border-b pb-2">{group.group}</h3>
                  <div className="overflow-x-auto">
                    <table className="w-full text-left text-sm">
                      <thead>
                        <tr>
                          <th className="w-2/5 py-3 font-medium text-muted-foreground">Feature</th>
                          <th className="w-1/5 py-3 font-medium text-muted-foreground">Startup</th>
                          <th className="w-1/5 py-3 font-medium text-muted-foreground text-indigo-600 dark:text-indigo-400">Growth</th>
                          <th className="w-1/5 py-3 font-medium text-muted-foreground">Enterprise</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-border">
                        {group.rows.map((row, j) => (
                          <tr key={j}>
                            <td className="py-3 font-medium">{row.feature}</td>
                            {row.values.map((val, k) => (
                              <td key={k} className={`py-3 ${k === 1 ? 'font-semibold text-indigo-600 dark:text-indigo-400' : 'text-slate-600 dark:text-slate-400'}`}>
                                {val}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <Faqs faqs={PRICING.faqs} />
    </Layout>
  );
}
